import React, { createContext, useCallback, useContext, useState } from 'react';

type Group = {
  id?: number;
  name?: string;
  image?: File | string;
};

type SimpleGroupContextData = Group & {
  isDrawerOpened?: boolean;
  handleOpenDrawer(): void;
  handleCloseDrawer(): void;
  handleResetGroup(): void;
  handleChangeGroup(group: Partial<Group>): void;
};

const SimpleGroupContext = createContext<SimpleGroupContextData>(
  {} as SimpleGroupContextData,
);

const SimpleGroupProvider: React.FC = ({ children }) => {
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean>();
  const [group, setGroup] = useState<Group>();

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpened(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpened(false);
  }, []);

  const handleChangeGroup = useCallback((partialGroup: Partial<Group>) => {
    setGroup((oldState) => ({ ...oldState, ...partialGroup }));
  }, []);

  const handleResetGroup = useCallback(() => {
    setGroup(undefined);
  }, []);

  return (
    <SimpleGroupContext.Provider
      value={{
        isDrawerOpened,
        handleResetGroup,
        handleOpenDrawer,
        handleChangeGroup,
        handleCloseDrawer,
        ...group,
      }}
    >
      {children}
    </SimpleGroupContext.Provider>
  );
};

function useSimpleGroup(): SimpleGroupContextData {
  const context = useContext(SimpleGroupContext);

  if (!context)
    throw new Error('useSimpleGroup must be used within SimpleGroupProvider');

  return context;
}

export { SimpleGroupProvider, useSimpleGroup };
